/**
 * ConsentPanel — waar een ouder toestemming geeft of intrekt.
 *
 * De tekst hier is geen bijzaak. Toestemming onder de AVG moet GEÏNFORMEERD
 * zijn: de ouder moet weten waarvoor hij tekent, in gewone taal, vóórdat hij
 * klikt. Daarom staat er expliciet wat er wél en niet wordt bewaard, en dat
 * intrekken altijd kan.
 *
 * TWEE OUDERS. Toestemming is een eigenschap van het KIND, niet van één ouder.
 * Zijn er twee gekoppeld, dan geldt één gedeelde toestemming. Dat moet
 * zichtbaar zijn, anders:
 *   - denkt ouder B dat hij nog iets moet doen terwijl het al geregeld is, of
 *   - trekt ouder B "zijn" toestemming in en wist daarmee ongemerkt de data
 *     waar ouder A voor tekende.
 *
 * Daarom tonen we bij toestemming van de ándere ouder een gedempte staat met
 * naam en datum, en zit intrekken achter een tweede, zwaardere stap. We
 * blokkeren het niet: juridisch mag elke gezagdrager intrekken. Maar het mag
 * geen ongeluk zijn.
 */

import React, { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert, Loader2, Users } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { SUPABASE_AUTH_ENABLED, fetchLinkedPeople, getActiveProfile, type LinkedPerson } from '../../../data/supabase/session';
import { grantConsent, withdrawConsent } from '../../../data/supabase/consent';
function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
}
export function ConsentPanel() {
  const [children, setChildren] = useState<LinkedPerson[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmWithdraw, setConfirmWithdraw] = useState<LinkedPerson | null>(null);
  const myId = getActiveProfile()?.id ?? null;
  const load = () => {
    void fetchLinkedPeople().then(people => setChildren(people.filter(p => p.relation === 'child')));
  };
  useEffect(load, []);
  if (!SUPABASE_AUTH_ENABLED) return null;
  const handleGrant = async (child: LinkedPerson) => {
    setBusyId(child.id);
    setNotice(null);
    const res = await grantConsent(child.id);
    setBusyId(null);
    setNotice(res.ok ? `Toestemming gegeven voor ${child.name}. Toetsresultaten worden vanaf nu bewaard.` : res.message ?? 'Er ging iets mis.');
    if (res.ok) load();
  };
  const handleWithdraw = async (child: LinkedPerson) => {
    setConfirmWithdraw(null);
    setBusyId(child.id);
    setNotice(null);
    const res = await withdrawConsent(child.id);
    setBusyId(null);
    if (res.ok) {
      const n = (res.deleted?.reports ?? 0) + (res.deleted?.attempts ?? 0);
      // Benoem wat er gewist is. "Ingetrokken" zonder gevolg klinkt als een
      // vinkje; de ouder moet weten dat er daadwerkelijk data is verwijderd.
      setNotice(n > 0 ? `Toestemming ingetrokken. ${n} bewaarde toetsgegevens zijn verwijderd. ${child.name} kan gewoon blijven oefenen.` : `Toestemming ingetrokken. Er waren nog geen toetsgegevens bewaard.`);
      load();
    } else {
      setNotice(res.message ?? 'Er ging iets mis.');
    }
  };
  return <section className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm" aria-labelledby="consent-heading" data-id="element-7956" data-mp="wbzp2wttkr8g">
      <h3 id="consent-heading" className="font-black text-slate-900 text-lg flex items-center gap-3 mb-2" data-id="element-7957" data-mp="rbv9ra4ne2jt">
        <ShieldCheck className="w-5 h-5 text-indigo-500" data-id="element-7958" data-mp="169e6aynkhk1t" />
        Toestemming voor het bewaren van gegevens
      </h3>
      <p className="text-sm text-slate-600 mb-5" data-id="element-7959" data-mp="1198cbbttrlgk">
        Oefenen werkt altijd — daar is geen toestemming voor nodig. Voor het{' '}
        <strong data-id="element-7960" data-mp="332zb46eqkzil">bewaren van toetsresultaten en rapporten</strong> vraagt de wet
        toestemming van een ouder, omdat dit een beoordeling van uw kind is. U
        kunt dit op elk moment intrekken; bewaarde resultaten worden dan
        verwijderd.
      </p>

      {notice && <div role="status" className="mb-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-700" data-id="element-7961" data-mp="243ydp10ynag9">
          {notice}
        </div>}

      {children === null ? <p className="text-sm text-slate-400" data-id="element-7962" data-mp="1bfsrsay67feu">Laden…</p> : children.length === 0 ? <p className="text-sm text-slate-600" data-id="element-7963" data-mp="1gdkve286gpyv">
          Er is nog geen kind aan uw account gekoppeld. Vraag uw kind om de
          koppelcode uit zijn instellingen.
        </p> : <ul className="space-y-3" data-id="element-7964" data-mp="1p6622jyjb67c">
          {children.map(child => {
        const granted = child.consentStatus === 'granted';
        const byMe = granted && child.consentGrantedBy === myId;
        const byOther = granted && !byMe;
        const otherName = child.consentGrantedByName ?? 'de andere ouder';
        const date = formatDate(child.consentGrantedAt);
        return <li key={child.id} className={`p-4 rounded-xl border ${byOther ? 'border-slate-200 bg-slate-50' : 'border-slate-200'}`} data-id="element-7965" data-mp="k79ee0za6k7i">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-7966" data-mp="b95sh3kdvlf6">
                  <div className="flex items-center gap-3" data-id="element-7967" data-mp="69ktwu94lf2z">
                    {granted ? <ShieldCheck className={`w-5 h-5 flex-shrink-0 ${byOther ? 'text-slate-400' : 'text-emerald-500'}`} data-id="element-7968" data-mp="1ex5eve4gkoe" /> : <ShieldAlert className="w-5 h-5 text-amber-500 flex-shrink-0" data-id="element-7969" data-mp="3ojmqgxbawr1b" />}
                    <div data-id="element-7970" data-mp="3joh5q1tmxiet">
                      <div className="font-bold text-slate-900" data-id="element-7971" data-mp="3en0587f2bxyr">
                        {child.name}
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-7972" data-mp="34z4n4937xvxt">
                        {granted ? 'Toetsresultaten worden bewaard.' : 'Alleen oefenvoortgang. Toetsresultaten blijven op het apparaat van uw kind.'}
                      </div>
                    </div>
                  </div>

                  {/* Gaf de ándere ouder toestemming, dan is er voor deze ouder
                      niets te doen. Een actieve knop zou suggereren dat er nog
                      iets moet gebeuren. */}
                  {byOther ? <span className="text-sm font-semibold text-slate-400 flex-shrink-0" data-id="element-7973" data-mp="3e1du4oza376g">
                      Al geregeld
                    </span> : <Button variant={granted ? 'secondary' : 'primary'} disabled={busyId === child.id} onClick={() => granted ? setConfirmWithdraw(child) : void handleGrant(child)} className="w-full sm:w-auto flex-shrink-0" data-id="element-7974" data-mp="ak5g6g7o8rh0">
                      {busyId === child.id ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-7975" data-mp="29sn6hsdq4e43" /> : granted ? 'Intrekken' : 'Toestemming geven'}
                    </Button>}
                </div>

                {/* Herkomst van de toestemming. Bij twee ouders is dit het
                    verschil tussen begrijpen en gissen. */}
                {byOther && <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-600" data-id="element-7976" data-mp="1lzyb5otybet2">
                    <p className="flex items-start gap-2" data-id="element-7977" data-mp="31gfc1mr0v6wh">
                      <Users className="w-4 h-4 mt-0.5 flex-shrink-0 text-slate-400" data-id="element-7978" data-mp="2xgy5sr3ru5q9" />
                      <span data-id="element-7979" data-mp="utzs7it15x9g">
                        <strong data-id="element-7980" data-mp="uust435b5sjo">{otherName}</strong> gaf op {date} toestemming
                        voor {child.name}. Er is één toestemming per kind, dus u
                        hoeft niets te doen — u ziet de resultaten gewoon.
                      </span>
                    </p>
                    <button type="button" onClick={() => setConfirmWithdraw(child)} className="mt-2 ml-6 text-sm font-semibold text-rose-600 hover:text-rose-700 underline underline-offset-2" data-id="element-7981" data-mp="19ivvl2k56m3q">
                      Toch intrekken
                    </button>
                    <p className="mt-1 ml-6 text-xs text-slate-500" data-id="element-7982" data-mp="1ngc4qlttotz6">
                      Dit geldt voor het hele account en verwijdert ook de
                      resultaten die {otherName} bewaart. Overleg dit eerst.
                    </p>
                  </div>}

                {byMe && date && <p className="mt-3 pt-3 border-t border-slate-200 text-sm text-slate-500" data-id="element-7983" data-mp="2bdqlfh2ikvdx">
                    U gaf toestemming op {date}.
                    {child.parentCount > 1 && ` Er is nog een ouder gekoppeld; intrekken geldt ook voor hem of haar.`}
                  </p>}
              </li>;
      })}
        </ul>}

      <ConfirmDialog open={confirmWithdraw !== null} title="Toestemming intrekken" message={confirmWithdraw ? confirmWithdraw.consentGrantedBy && confirmWithdraw.consentGrantedBy !== myId ? `${confirmWithdraw.consentGrantedByName ?? 'De andere ouder'} gaf deze toestemming. Als u intrekt, worden alle bewaarde toetsresultaten en rapporten van ${confirmWithdraw.name} verwijderd — ook voor de andere ouder. Dit kan niet ongedaan worden gemaakt. ${confirmWithdraw.name} kan daarna gewoon blijven oefenen.` : `Alle bewaarde toetsresultaten en rapporten van ${confirmWithdraw.name} worden verwijderd. Dit kan niet ongedaan worden gemaakt. ${confirmWithdraw.name} kan daarna gewoon blijven oefenen.` : ''} confirmLabel="Intrekken en verwijderen" cancelLabel="Annuleren" variant="danger" onConfirm={() => confirmWithdraw && void handleWithdraw(confirmWithdraw)} onCancel={() => setConfirmWithdraw(null)} data-id="element-7984" data-mp="1xk93u1te1sqj" />
    </section>;
}